/**
 * Étape 3 — le récapitulatif d'envoi (chunk 9.AZ.1).
 *
 * ## L'élément qui porte tout l'écran
 *
 * C'est la pièce que le courtier pilote cherchait sans la trouver : une phrase
 * simple qui dit **ce qui part**, **à qui**, et **avec quoi**. Un envoi vers un
 * assureur est un geste engageant — il n'y a pas de rappel, et l'assureur juge le
 * cabinet sur ce qu'il reçoit. L'écran doit donc rendre cela lisible d'un regard,
 * avant le clic, et pas après.
 *
 * Tout le reste de la page est délibérément sobre pour que ce bloc porte seul
 * l'attention : un filet vertical, une énumération en toutes lettres, un seul
 * bouton primaire.
 *
 * ## Ce qu'il ne prétend pas faire — 9.BJ.1 le rend vrai
 *
 * Il n'anticipe aucun refus du serveur. Jusqu'à ce chunk, la phrase était une
 * intention démentie par le code : `pret` recalculait ici une partie de la règle
 * d'envoi, et elle a fini par en dire moins que le serveur — ni le mandat signé
 * ni le cahier obligatoire n'y figuraient.
 *
 * Le verdict vient maintenant de `GET /v1/quote-requests/:id/readiness`, produit
 * par la MÊME fonction serveur qui refuse l'envoi. L'écran affiche des codes
 * traduits par le registre bilingue, et n'a plus une seule règle à lui.
 */

import type { QuoteRequestRecipientRow, SendReadiness } from "@kollectivia/shared";
import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { PreparedAttachment } from "../../../components/PreparedAttachment";
import { useInsurerCatalog } from "../../../lib/queries/insurers";

import { Etape } from "./Etape";
import { SendBlockers } from "./SendBlockers";

export function EnvoiRecapitulatif(props: {
  /**
   * Le rang de l'étape dans le parcours.
   *
   * Paramétré depuis 9.BK.3 : l'étape de l'effectif s'intercale quand le
   * dossier en porte un et **disparaît** sinon. Un numéro codé en dur
   * produirait une suite trouée — 1, 3, 4 — qui ferait chercher l'étape
   * absente. La page sait quelles étapes elle rend ; elle les numérote.
   */
  numero?: number;
  censusExport?: ReactNode;
  aRefaire: boolean;
  destinataires: readonly QuoteRequestRecipientRow[];
  echeance: string;
  onEcheance: (v: string) => void;
  /** Sauvegarde explicite, avant envoi ; aucun PATCH au simple blur. */
  onEcheanceValidee: (v: string) => void;
  enVol: boolean;
  uncommittedChanges?: boolean;
  onEnvoyer: () => void;
  /**
   * 9.BJ.1 — le verdict du serveur. `undefined` pendant le chargement : on
   * n'active pas l'envoi tant qu'on ne sait pas, plutôt que de supposer que oui.
   */
  readiness: SendReadiness | undefined;
  /** Keep saved preview nodes mounted during passive revalidation. */
  readinessRefreshing?: boolean;
  readinessLoading?: boolean;
}) {
  const { t } = useTranslation();
  const { labelFor } = useInsurerCatalog();
  const champEcheance = useId();

  const joignables = props.destinataires.filter(
    (d) => !!d.recipient_email && d.status === "pending",
  );
  const pieces = props.readiness?.delivery?.attachments;
  // Le seul juge. Tant que la lecture n'a pas répondu, on n'envoie pas.
  //
  // `aRefaire` s'y ajoute, et ce n'est PAS une règle recalculée : c'est la
  // mémoire d'un refus que le serveur vient de donner sur un cahier périmé. Ne
  // pas s'en souvenir rendrait le bouton actif juste après l'avoir vu refuser.
  const pret = props.readiness?.can_send === true && !props.aRefaire;
  const blocages = props.readiness?.blockers ?? [];

  return (
    <Etape
      numero={props.numero ?? 3}
      titre={t("prepare_send.send.title")}
      description={t("prepare_send.send.subtitle")}
      etat={pret && !props.uncommittedChanges ? "ready" : "empty"}
    >
      {/* Le bon d'envoi : ce qui part, à qui, avec quoi. En toutes lettres. */}
      <div className="border-brand-600 rounded-r-lg border-l-4 bg-slate-50 px-4 py-4">
        <ul className="space-y-2 text-sm text-slate-800">
          <li className="flex flex-col gap-2 md:flex-row">
            <span className="shrink-0 text-slate-500">{t("prepare_send.send.what")}</span>
            <div className="min-w-0 font-medium">
              <p>
                {pieces === undefined
                  ? t(
                      props.readinessLoading
                        ? "delivery.email_preview.loading"
                        : "prepare_send.send.what_unavailable",
                    )
                  : pieces.length > 0
                    ? t("prepare_send.send.what_ready", { count: pieces.length })
                    : t("prepare_send.send.what_missing")}
              </p>
              {pieces && pieces.length > 0 && (
                <ul className="mt-1 space-y-1" aria-label={t("delivery.attachments")}>
                  {pieces.map((piece) => (
                    <li key={piece.file_id} className="break-words [overflow-wrap:anywhere]">
                      <PreparedAttachment file={piece} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </li>
          <li className="flex gap-2">
            <span className="shrink-0 text-slate-500">{t("prepare_send.send.to_whom")}</span>
            <span className="min-w-0 font-medium [overflow-wrap:anywhere]">
              {joignables.length > 0
                ? joignables
                    .map((d) => `${labelFor(d.insurer_code)} — ${d.recipient_email}`)
                    .join(", ")
                : t("prepare_send.send.to_whom_missing")}
            </span>
          </li>
          <li className="flex gap-2">
            <span className="shrink-0 text-slate-500">{t("prepare_send.send.how")}</span>
            <span className="font-medium">
              {t("prepare_send.send.how_individual", { count: joignables.length })}
            </span>
          </li>
        </ul>
      </div>

      <section className="mt-4 min-w-0 space-y-3" aria-label={t("delivery.email_preview.title")}>
        <h3 className="font-semibold">{t("delivery.email_preview.title")}</h3>
        <p className="text-sm text-slate-600">{t("delivery.email_preview.saved")}</p>
        {props.uncommittedChanges ? (
          <p role="status" className="text-sm text-amber-800">
            {t("delivery.email_preview.unsaved")}
          </p>
        ) : props.readiness?.delivery?.email_previews === undefined ? (
          <p className="text-sm text-slate-600">
            {t(
              props.readinessLoading
                ? "delivery.email_preview.loading"
                : "delivery.email_preview.unavailable",
            )}
          </p>
        ) : props.readiness.delivery.email_previews.length === 0 ? (
          <p className="text-sm text-slate-600">{t("delivery.email_preview.empty")}</p>
        ) : (
          props.readiness.delivery.email_previews.map((preview) => (
            <details
              key={preview.recipient_id}
              className="min-w-0 rounded-lg border border-slate-200 p-3 [overflow-wrap:anywhere]"
            >
              <summary className="min-h-11 cursor-pointer font-medium">
                {labelFor(preview.insurer_code)} —{" "}
                {preview.to_email ?? t("delivery.email_preview.no_address")} (
                {preview.language.toUpperCase()})
              </summary>
              {preview.rendered ? (
                <div className="mt-3 space-y-3">
                  <p className="text-sm">
                    <strong>{t("delivery.email_preview.subject")} </strong>
                    {preview.rendered.subject}
                  </p>
                  <pre className="whitespace-pre-wrap break-words font-sans text-sm">
                    {preview.rendered.text}
                  </pre>
                  <details>
                    <summary className="min-h-11 cursor-pointer text-sm underline">
                      {t("delivery.email_preview.html")}
                    </summary>
                    <iframe
                      title={t("delivery.email_preview.frame", {
                        recipient: preview.to_email ?? labelFor(preview.insurer_code),
                      })}
                      sandbox=""
                      referrerPolicy="no-referrer"
                      srcDoc={`<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'">${preview.rendered.html}`}
                      className="h-80 w-full rounded border border-slate-200 bg-white"
                    />
                  </details>
                </div>
              ) : (
                <p className="text-sm text-slate-600">{t("delivery.email_preview.unavailable")}</p>
              )}
            </details>
          ))
        )}
      </section>

      <div className="mt-4">
        <label htmlFor={champEcheance} className="label">
          {t("prepare_send.send.deadline")}
        </label>
        <input
          id={champEcheance}
          type="date"
          value={props.echeance}
          onChange={(e) => props.onEcheance(e.target.value)}
          disabled={props.enVol}
          required
          className="input max-w-xs"
          aria-describedby={`${champEcheance}-aide`}
        />
        <button
          type="button"
          className="btn-secondary mt-2 min-h-11"
          disabled={props.enVol}
          onClick={() => props.onEcheanceValidee(props.echeance)}
        >
          {t("request_flow.save_deadline")}
        </button>
        <p id={`${champEcheance}-aide`} className="mt-1 text-xs text-slate-500">
          {t("prepare_send.send.deadline_help")}
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
        {props.uncommittedChanges && (
          <p role="status" className="w-full text-sm text-amber-800">
            {t("preparation_context.unsaved")}
          </p>
        )}
        <button
          type="button"
          disabled={!pret || props.enVol || props.uncommittedChanges || props.readinessRefreshing}
          aria-busy={props.enVol}
          data-guide-target="followup"
          onClick={props.onEnvoyer}
          className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 motion-reduce:transition-none"
        >
          {props.enVol
            ? t("prepare_send.send.sending")
            : t("prepare_send.send.cta", { count: joignables.length })}
        </button>
        {/* Ce qui bloque, TOUT ce qui bloque, dans les mots du serveur.
            Les découvrir un par un, à chaque tentative refusée, est la façon la
            plus sûre de faire abandonner quelqu'un. */}
        {!pret && <SendBlockers blockers={blocages}>{props.censusExport}</SendBlockers>}
      </div>
    </Etape>
  );
}
